// src/stores/authStore.ts - Enhanced with onboarding logic based on your working code
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
  signInWithRedirect,
  type AuthUser
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { apiClient } from '@/app-lib/api-client';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  timezone: string;
  subscription_tier: 'free' | 'pro' | 'enterprise';
  created_at: string;
  updated_at: string;
  settings?: Record<string, any>;
  // OAuth profile data
  picture?: string;
  given_name?: string;
  family_name?: string;
  email_verified?: boolean;
  provider?: 'cognito' | 'google';
  // ADDED: Onboarding fields
  onboarding_completed: boolean;
  onboarding_step?: number;
  // Additional onboarding data fields
  business_name?: string;
  youtube_channel_url?: string;
  content_niche?: string;
  videos_per_week?: number;
  primary_goal?: string;
  experience_level?: string;
  onboarded_at?: string;
}

interface AuthState {
  // Authentication state
  user: AuthUser | null;
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // OTP verification state
  pendingVerification: {
    email: string;
    password: string;
    name: string;
  } | null;

  // ADDED: Onboarding helpers
  shouldRedirectToOnboarding: () => boolean;
  getPostAuthRedirectPath: () => string;

  // Actions (UPDATED: signIn returns redirect path)
  signIn: (email: string, password: string) => Promise<string>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  confirmSignUp: (email: string, confirmationCode: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, confirmationCode: string, newPassword: string) => Promise<void>;
  checkAuthState: () => Promise<void>;
  clearError: () => void;

  // OAuth actions (UPDATED: handleOAuthCallback returns redirect path)
  signInWithGoogle: () => Promise<void>;
  handleOAuthCallback: () => Promise<string>;

  // Profile actions
  fetchUserProfile: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  // ADDED: Onboarding completion
  completeOnboarding: (onboardingData: any) => Promise<void>;
}

// Helper function to extract user attributes safely
const getUserAttributes = async (user: AuthUser) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;

    if (idToken) {
      // Extract attributes from ID token payload
      const payload = idToken.payload;
      return {
        email: payload.email as string,
        name: payload.name as string,
        given_name: payload.given_name as string,
        family_name: payload.family_name as string,
        picture: payload.picture as string,
        email_verified: payload.email_verified as boolean,
        // Check if user signed in via federated identity (Google)
        identities: payload.identities as any,
      };
    }

    // Fallback: try to get basic info from user object
    return {
      email: user.signInDetails?.loginId || user.username || '',
      name: user.username || 'User',
      given_name: '',
      family_name: '',
      picture: '',
      email_verified: false,
      identities: null,
    };
  } catch (error) {
    console.error('Error extracting user attributes:', error);
    return {
      email: user.signInDetails?.loginId || user.username || '',
      name: user.username || 'User',
      given_name: '',
      family_name: '',
      picture: '',
      email_verified: false,
      identities: null,
    };
  }
};

// Secure token storage - FIXED: Use localStorage for persistent auth across browser sessions
const secureStorage = createJSONStorage(() => {
  return {
    getItem: (name: string) => {
      const item = localStorage.getItem(name);
      return item ? JSON.parse(item) : null;
    },
    setItem: (name: string, value: any) => {
      localStorage.setItem(name, JSON.stringify(value));
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name);
    },
  };
});

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      userProfile: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingVerification: null,

      // ADDED: Onboarding navigation helpers
      shouldRedirectToOnboarding: () => {
        const { userProfile, isAuthenticated } = get();

        // Return false if not authenticated or no profile loaded yet
        if (!isAuthenticated || !userProfile) {
          return false;
        }

        console.log('🔍 Checking onboarding status:', {
          userProfile: userProfile,
          onboarding_completed: userProfile.onboarding_completed,
          settings_onboardingCompleted: userProfile.settings?.onboardingCompleted,
          final_decision: 'Will check both formats...'
        });

        // Check both possible formats of onboarding completion
        const onboardingCompleted = userProfile.onboarding_completed || userProfile.settings?.onboardingCompleted;

        console.log('✅ Final onboarding decision:', {
          onboardingCompleted,
          needsOnboarding: !onboardingCompleted
        });

        // Return true if user needs onboarding (onboarding NOT completed)
        return !onboardingCompleted;
      },

      getPostAuthRedirectPath: () => {
        const { userProfile } = get();

        // Fallback if no profile loaded yet
        if (!userProfile) {
          return '/';
        }

        // Check both possible formats of onboarding completion
        const onboardingCompleted = userProfile.onboarding_completed || userProfile.settings?.onboardingCompleted;

        console.log('🎯 Navigation decision:', {
          onboarding_completed: userProfile.onboarding_completed,
          settings_onboardingCompleted: userProfile.settings?.onboardingCompleted,
          final_onboardingCompleted: onboardingCompleted,
          redirect_to: onboardingCompleted ? '/' : '/onboarding'
        });

        // New user needs onboarding
        if (!onboardingCompleted) {
          return '/onboarding';
        }

        // Returning user goes to dashboard
        return '/';
      },      // UPDATED: signIn now returns redirect path
      signIn: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const result = await signIn({
            username: email,
            password,
          });

          if (result.isSignedIn) {
            const user = await getCurrentUser();
            const session = await fetchAuthSession();

            if (session.tokens) {
              apiClient.setToken(session.tokens.idToken?.toString() || '');
            }

            set({ user, isAuthenticated: true });

            // Fetch user profile to determine redirect path
            await get().fetchUserProfile();

            // Return the appropriate redirect path
            return get().getPostAuthRedirectPath();
          } else {
            throw new Error('Sign in incomplete');
          }
        } catch (error: any) {
          console.error('Sign in error:', error);
          set({
            error: error.message || 'Sign in failed',
            isAuthenticated: false,
            user: null,
            userProfile: null
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      signUp: async (email: string, password: string, name: string) => {
        try {
          set({ isLoading: true, error: null });

          await signUp({
            username: email,
            password,
            options: {
              userAttributes: {
                email,
                name,
                given_name: name.split(' ')[0],
                family_name: name.split(' ').slice(1).join(' ') || name.split(' ')[0],
              },
            },
          });

          // Store verification data for OTP screen
          set({
            pendingVerification: { email, password, name }
          });

        } catch (error: any) {
          console.error('Sign up error:', error);
          set({ error: error.message || 'Sign up failed' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      confirmSignUp: async (email: string, confirmationCode: string) => {
        try {
          set({ isLoading: true, error: null });

          await confirmSignUp({
            username: email,
            confirmationCode,
          });

          // Clear pending verification
          set({ pendingVerification: null });

        } catch (error: any) {
          console.error('Confirm sign up error:', error);
          set({ error: error.message || 'Confirmation failed' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      resendConfirmationCode: async (email: string) => {
        try {
          set({ isLoading: true, error: null });
          await resendSignUpCode({ username: email });
        } catch (error: any) {
          console.error('Resend confirmation code error:', error);
          set({ error: error.message || 'Failed to resend code' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      signOut: async () => {
        try {
          // Clear API client token first
          apiClient.clearToken();
          
          // Sign out from Cognito
          await signOut({ global: true });
          
        } catch (error: any) {
          console.error('Sign out error:', error);
        } finally {
          // Always clear everything regardless of signOut success
          set({
            user: null,
            userProfile: null,
            isAuthenticated: false,
            error: null,
            pendingVerification: null,
            isLoading: false,
          });
          
          // Clear all storage
          localStorage.clear();
          sessionStorage.clear();
          
          // Small delay to ensure state is cleared
          setTimeout(() => {
            window.location.replace('/auth/signin');
          }, 100);
        }
      },

      resetPassword: async (email: string) => {
        try {
          set({ isLoading: true, error: null });
          await resetPassword({ username: email });
        } catch (error: any) {
          console.error('Reset password error:', error);
          set({ error: error.message || 'Password reset failed' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      confirmResetPassword: async (email: string, confirmationCode: string, newPassword: string) => {
        try {
          set({ isLoading: true, error: null });
          await confirmResetPassword({
            username: email,
            confirmationCode,
            newPassword,
          });
        } catch (error: any) {
          console.error('Confirm reset password error:', error);
          set({ error: error.message || 'Password confirmation failed' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Google OAuth method
      signInWithGoogle: async () => {
        try {
          set({ isLoading: true, error: null });

          // Check if Google OAuth is enabled
          const isGoogleEnabled = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED === 'true';

          if (!isGoogleEnabled) {
            throw new Error('Google sign-in is not configured');
          }

          console.log('Initiating Google sign-in redirect...');

          // Use signInWithRedirect - this will redirect the page
          await signInWithRedirect({
            provider: 'Google'
          });

          // Note: Code after this won't execute as the page redirects
        } catch (error: any) {
          console.error('Google sign-in error:', error);
          set({
            error: error.message || 'Google sign-in failed',
            isLoading: false
          });
          throw error;
        }
      },

      // UPDATED: Enhanced OAuth callback handler that returns redirect path
      handleOAuthCallback: async () => {
        // SIMPLIFIED: Let the OAuthCallback component handle everything
        // This method is now just a placeholder since we handle OAuth directly in the component
        console.log('⚠️  handleOAuthCallback called - using direct component approach instead');
        return '/'; // Default fallback
      },

      setAuthState: (authData: {
        user: AuthUser | null;
        userProfile: any;
        isAuthenticated: boolean;
      }) => {
        set({
          user: authData.user,
          userProfile: authData.userProfile,
          isAuthenticated: authData.isAuthenticated,
          isLoading: false,
          error: null
        });

        console.log('🔄 Auth state manually updated:', {
          isAuthenticated: authData.isAuthenticated,
          hasProfile: !!authData.userProfile,
          onboardingCompleted: authData.userProfile?.onboarding_completed
        });
      },
      checkAuthState: async () => {
        try {
          set({ isLoading: true });

          const user = await getCurrentUser();
          const session = await fetchAuthSession();

          if (user && session.tokens) {
            // Store token for API client
            const idToken = session.tokens.idToken?.toString();
            if (idToken) {
              localStorage.setItem('auth_token', idToken);
            }

            set({
              user,
              isAuthenticated: true
            });

            // Set API client token
            apiClient.setToken(idToken || '');

            // Fetch user profile
            await get().fetchUserProfile();
          } else {
            // Clear stored tokens
            localStorage.removeItem('auth_token');
            sessionStorage.removeItem('auth_token');

            set({
              user: null,
              userProfile: null,
              isAuthenticated: false
            });
          }
        } catch (error: any) {
          // Silently handle unauthenticated state - this is expected after logout
          if (error.name !== 'UserUnAuthenticatedException') {
            console.error('Auth check error:', error);
          }
          localStorage.removeItem('auth_token');
          sessionStorage.removeItem('auth_token');
          set({
            user: null,
            userProfile: null,
            isAuthenticated: false
          });
        } finally {
          set({ isLoading: false });
        }
      },
      clearError: () => set({ error: null }),

      fetchUserProfile: async () => {
        try {
          console.log('👤 Fetching user profile...');
          const response = await apiClient.get('/api/users/profile');
          console.log('✅ User profile fetched:', response.data);
          set({ userProfile: response.data });
        } catch (error: any) {
          console.error('❌ Fetch user profile error:', error);

          // If profile doesn't exist, create one
          if (error.response?.status === 404) {
            try {
              console.log('🆕 Creating new user profile...');
              const user = get().user;
              if (user) {
                // Extract user attributes safely
                const userAttributes = await getUserAttributes(user);
                console.log('📝 Extracted user attributes:', userAttributes);

                const userData = {
                  email: userAttributes.email || user.signInDetails?.loginId || user.username || '',
                  name: userAttributes.name || userAttributes.given_name || 'User',
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  // Include OAuth profile data if available
                  ...(userAttributes.picture && { picture: userAttributes.picture }),
                  ...(userAttributes.given_name && { given_name: userAttributes.given_name }),
                  ...(userAttributes.family_name && { family_name: userAttributes.family_name }),
                  ...(userAttributes.email_verified !== undefined && { email_verified: userAttributes.email_verified }),
                  // Detect provider based on identities
                  provider: userAttributes.identities ? 'google' : 'cognito',
                  // New users start with onboarding incomplete
                  onboarding_completed: false,
                  onboarding_step: 1
                };

                console.log('🔧 Creating user profile with data:', userData);
                const createResponse = await apiClient.post('/api/users/profile', userData);
                console.log('✅ User profile created:', createResponse.data);
                set({ userProfile: createResponse.data });
              }
            } catch (createError) {
              console.error('💥 Create user profile error:', createError);
            }
          }
        }
      },


      updateUserProfile: async (updates: Partial<UserProfile>) => {
        try {
          const response = await apiClient.put('/api/users/profile', updates);
          set({ userProfile: response.data });
        } catch (error: any) {
          console.error('Update user profile error:', error);
          throw error;
        }
      },

      // ADDED: Complete onboarding method
      completeOnboarding: async (onboardingData: any) => {
        try {
          const updates = {
            ...onboardingData,
            onboarding_completed: true,
            onboarding_step: null,
            onboarded_at: new Date().toISOString()
          };

          console.log('Completing onboarding with data:', updates);
          await get().updateUserProfile(updates);
        } catch (error: any) {
          console.error('Complete onboarding error:', error);
          throw error;
        }
      },
    }),
    {
      name: 'tubeops-auth',
      storage: secureStorage,
      partialize: (state) => ({
        // Only persist essential auth state
        isAuthenticated: state.isAuthenticated,
        userProfile: state.userProfile,
        pendingVerification: state.pendingVerification,
      }),
    }
  )
);

// Add this to the top of your authStore.ts file (outside the store definition):
let isOAuthFlow = false;

// Then replace your Hub listener with this version:

Hub.listen('auth', (hubData) => {
  const { payload } = hubData;
  const { event } = payload;

  console.log('Auth Hub Event:', event, payload);

  switch (event) {
    case 'signInWithRedirect':
      console.log('Sign in with redirect initiated via Hub', payload);
      // ADDED: Flag that we're starting an OAuth flow
      isOAuthFlow = true;
      console.log('🔄 OAuth flow started, flagged for navigation');
      break;

    case 'signedIn':
      console.log('User signed in via Hub', payload);

      // FIXED: Use the OAuth flag instead of URL checking
      setTimeout(async () => {
        try {
          console.log('🚀 Starting post-signin navigation logic...');
          console.log('🏁 OAuth flow flag:', isOAuthFlow);

          if (isOAuthFlow) {
            console.log('✅ OAuth flow detected via flag, processing navigation...');

            // Reset the flag
            isOAuthFlow = false;

            // Get current auth state
            const state = useAuthStore.getState();

            // Ensure we have the latest profile data
            console.log('📡 Fetching latest auth state...');
            await state.checkAuthState();

            // Get updated state after profile fetch
            const updatedState = useAuthStore.getState();
            const { userProfile, isAuthenticated } = updatedState;

            console.log('📊 OAuth auth state check:', {
              isAuthenticated,
              hasProfile: !!userProfile,
              profileData: userProfile
            });

            if (isAuthenticated && userProfile) {
              // Check onboarding status from your actual profile structure
              const onboardingCompleted = userProfile.onboarding_completed || userProfile.settings?.onboardingCompleted;

              const destination = onboardingCompleted ? '/' : '/onboarding';

              console.log('🎯 OAuth Navigation Decision:', {
                onboarding_completed: userProfile.onboarding_completed,
                settings_onboardingCompleted: userProfile.settings?.onboardingCompleted,
                final_onboardingCompleted: onboardingCompleted,
                destination: destination,
                currentUrl: window.location.href
              });

              console.log(`🚀 OAuth navigating from ${window.location.href} to: ${destination}`);

              // Show success message
              import('react-hot-toast').then(({ default: toast }) => {
                toast.success(onboardingCompleted ? 'Welcome back to TubeOps!' : 'Welcome to TubeOps! Let\'s get started.');
              });

              // Force navigation with multiple methods
              console.log('🔄 Method 1: window.location.href');
              window.location.href = destination;

              // Backup method
              setTimeout(() => {
                if (window.location.pathname !== destination && window.location.pathname !== '/') {
                  console.log('🔄 Method 2: window.location.replace');
                  window.location.replace(destination);
                }
              }, 1000);

            } else if (isAuthenticated && !userProfile) {
              console.log('⚠️ Authenticated but no profile, redirecting to onboarding');
              window.location.href = '/onboarding';
            } else {
              console.log('❌ Not fully authenticated after OAuth');
              console.log('🔄 Fallback: redirecting to dashboard');
              window.location.href = '/';
            }
          } else {
            console.log('ℹ️ Regular sign-in (not OAuth), letting normal flow handle navigation');
          }

        } catch (error) {
          console.error('💥 Post-signin navigation error:', error);

          // Reset OAuth flag on error
          isOAuthFlow = false;

          // Force redirect to dashboard on error
          console.log('🔄 Error fallback, redirecting to dashboard');
          window.location.href = '/';
        }
      }, 3000); // Increased wait time

      // Also trigger checkAuthState for non-OAuth flows
      if (!isOAuthFlow) {
        useAuthStore.getState().checkAuthState();
      }
      break;

    case 'signedOut':
      console.log('User signed out via Hub', payload);
      isOAuthFlow = false;
      
      // Clear all state
      useAuthStore.setState({
        user: null,
        userProfile: null,
        isAuthenticated: false,
        error: null,
        pendingVerification: null,
      });
      
      // Clear storage
      localStorage.clear();
      sessionStorage.clear();
      break;

    case 'tokenRefresh':
      console.log('Token refreshed via Hub', payload);
      fetchAuthSession().then((session) => {
        if (session.tokens) {
          apiClient.setToken(session.tokens.idToken?.toString() || '');
        }
      }).catch((error) => {
        console.error('Error setting token after refresh:', error);
      });
      break;

    case 'tokenRefresh_failure':
      console.error('Token refresh failed via Hub', payload);
      useAuthStore.getState().signOut();
      break;

    case 'signInWithRedirect_failure':
      console.error('Sign in with redirect failed via Hub', payload);
      isOAuthFlow = false; // Reset flag on failure
      break;

    case 'customOAuthState':
      console.log('Custom OAuth state via Hub', payload);
      break;

    default:
      console.log('Unhandled auth event:', event, payload);
      break;
  }
});